import React, { useState } from 'react';
import {
  parseAddress,
  formatFullDate,
  getInitials,
  avatarColor,
  getOutgoingMailbox,
  getScheduledSendAt,
} from '../utils/emailParsing';

/**
 * MailDetailHeader — the top of the reading pane: avatar, sender/recipient,
 * subject, date and the primary action toolbar (Reply / Forward / Delete).
 *
 * Action handlers are passed in; this component stays presentational. The
 * `canReply` flag hides reply for outgoing/deleted views.
 */
export default function MailDetailHeader({
  email,
  isOutgoing,
  canReply,
  onReply,
  onForward,
  onDelete,
  deleteLabel = 'Delete',
  onSendNow,
  sendingNow,
  onResendAsIntended,
  onResendAll,
  resendLoading = false,
  onBlock,
  onAddToLeads,
  onExportThread,
  exportLabel = 'Chatverlauf exportieren',
  onClose,
  deleting,
  blocking,
  showMeta = true,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const isScheduled = email.isScheduled === true;
  const personField = isOutgoing
    ? (email.to || email.recipientEmail || email.recipients || '')
    : (email.from || email.senderEmail || '');
  const person = parseAddress(personField);
  const scheduledAt = isScheduled ? getScheduledSendAt(email) : null;
  const date = isScheduled
    ? (scheduledAt ? formatFullDate(scheduledAt) : '')
    : formatFullDate(email.timestamp || email.date);
  const mailbox = isScheduled ? getOutgoingMailbox(email) : (email.accountEmail || '');
  const showMailbox = !!mailbox && (isScheduled || !isOutgoing);

  // Prefer the contact's business name as the headline (falls back to the
  // parsed name, then the bare address). The address itself is shown on the
  // To/From line below.
  const businessName = (email.businessName || email.business_name || '').trim();
  const displayName = businessName || person.name || person.email || 'Unknown';

  // Blocking targets the sender, so it only makes sense for incoming mail.
  const canBlock = Boolean(onBlock) && !isOutgoing;
  // Adding to Leads also targets the sender → incoming mail only.
  const canAddToLeads = Boolean(onAddToLeads) && !isOutgoing;
  const hasMenu = Boolean(onDelete || onForward || canBlock || canAddToLeads || onExportThread || onResendAsIntended || onResendAll);
  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="ivx-detail-header">
      <div className="ivx-detail-subject-row">
        {onClose && (
          <button type="button" className="ivx-detail-back" onClick={onClose} title="Back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}
        <h2 className="ivx-detail-subject">{email.subject || '(No subject)'}</h2>

        <div className="ivx-detail-actions">
          {hasMenu && (
            <div className="ivx-more-wrap">
              <button
                type="button"
                className="ivx-action-btn ghost ivx-more-btn"
                onClick={() => setMenuOpen((v) => !v)}
                title="More actions"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <circle cx="5" cy="12" r="1.6" fill="currentColor" />
                  <circle cx="12" cy="12" r="1.6" fill="currentColor" />
                  <circle cx="19" cy="12" r="1.6" fill="currentColor" />
                </svg>
              </button>
              {menuOpen && (
                <>
                  <div className="ivx-more-backdrop" onClick={closeMenu} />
                  <div className="ivx-more-menu">
                    {onResendAsIntended && (
                      <button type="button" className="ivx-more-item" onClick={() => { closeMenu(); onResendAsIntended(); }} disabled={resendLoading}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M3 11l18-8-8 18-2-7-8-3z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                        </svg>
                        Send as intended (Normal mode)
                      </button>
                    )}
                    {onResendAll && (
                      <button type="button" className="ivx-more-item" onClick={() => { closeMenu(); onResendAll(); }} disabled={resendLoading}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M4 6h16M4 12h16M4 18h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                        Send all SafeGuard mails (Bulk mode)
                      </button>
                    )}
                    {onForward && (
                      <button type="button" className="ivx-more-item" onClick={() => { closeMenu(); onForward(); }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M14 9V5l7 7-7 7v-4C9 16 6 17.5 4 20c1-5 4-9 10-11z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                        </svg>
                        Forward
                      </button>
                    )}
                    {canAddToLeads && (
                      <button type="button" className="ivx-more-item" onClick={() => { closeMenu(); onAddToLeads(); }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M15 19a4 4 0 00-4-4H6a4 4 0 00-4 4M9 11a3.5 3.5 0 100-7 3.5 3.5 0 000 7zM19 8v6M22 11h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Add to Leads
                      </button>
                    )}
                    {onExportThread && (
                      <button type="button" className="ivx-more-item" onClick={() => { closeMenu(); onExportThread(); }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M7 8V4h10v4M7 18H5a1 1 0 01-1-1v-6a1 1 0 011-1h14a1 1 0 011 1v6a1 1 0 01-1 1h-2M7 15h10v5H7v-5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {exportLabel}
                      </button>
                    )}
                    {canBlock && (
                      <button type="button" className="ivx-more-item danger" onClick={() => { closeMenu(); onBlock(); }} disabled={blocking}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                          <path d="M5.6 5.6l12.8 12.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                        {blocking ? 'Blocking…' : 'Block contact'}
                      </button>
                    )}
                    {onDelete && (
                      <button type="button" className="ivx-more-item danger" onClick={() => { closeMenu(); onDelete(); }} disabled={deleting}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M6 7h12M9 7V5h6v2m-7 0v12a1 1 0 001 1h6a1 1 0 001-1V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {deleting ? 'Deleting…' : deleteLabel}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
          {canReply && (
            <button type="button" className="ivx-action-btn" onClick={onReply} title="Reply">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M10 9V5l-7 7 7 7v-4c5 0 8 1.5 10 4-1-5-4-9-10-10z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
              </svg>
              Reply
            </button>
          )}
          {onSendNow && (
            <button type="button" className="ivx-action-btn" onClick={onSendNow} title="Send now" disabled={sendingNow}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M3 11l18-8-8 18-2-7-8-3z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
              </svg>
              {sendingNow ? 'Sending…' : 'Send now'}
            </button>
          )}
        </div>
      </div>

      {showMeta && (
      <div className="ivx-detail-meta">
        <div
          className="ivx-detail-avatar"
          style={{ backgroundColor: avatarColor(person.email || displayName) }}
        >
          {getInitials(displayName)}
        </div>
        <div className="ivx-detail-meta-text">
          <div className="ivx-detail-from">
            <span className="ivx-detail-from-name">{displayName}</span>
          </div>
          {/* One combined meta row: address + date + mailbox — three pieces
              of info that used to live on three separate lines. Keeping them
              inline shrinks the header without hiding anything. */}
          <div className="ivx-detail-date ivx-detail-meta-inline">
            <span className="ivx-detail-meta-part">
              {isOutgoing ? 'To: ' : 'From: '}
              {person.email && <span className="ivx-detail-from-email">{person.email}</span>}
            </span>
            {date && (
              <>
                <span className="ivx-detail-meta-sep">·</span>
                <span className="ivx-detail-meta-part">
                  {isScheduled ? `Sends ${date}` : date}
                </span>
              </>
            )}
            {showMailbox && (
              <>
                <span className="ivx-detail-meta-sep">·</span>
                <span
                  className="ivx-detail-mailbox ivx-detail-meta-part"
                  title={isScheduled ? `Sends from ${mailbox}` : `Received at ${mailbox}`}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path d="M3 7l9 6 9-6M4 5h16a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V6a1 1 0 011-1z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {isScheduled ? 'From' : 'To'} <strong>{mailbox}</strong>
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
